import fetch from '@adc/fetch'
import type { FieldChange, FieldChangeTimeline } from '@adc/sdk/types'
import { GRAPH_PROPERTY_HISTORY_QUERY } from '../queries/getPropertyHistory'

// First-step scope: only these fields are surfaced in Property History.
export const PROPERTY_HISTORY_FIELDS = [
  'listing_configuration/occupancy_status',
  'auction/start_date',
]

// A single change flattened out of a FieldChangeTimeline, with the graph's
// JSON-stringified previous/current values parsed back into their raw form.
export interface PropertyHistoryChange
  extends Pick<FieldChange, 'changed_at' | 'delta_label'> {
  field: string
  previous_value: unknown
  current_value: unknown
}

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || v === ''

// The graph returns previous/current values JSON.stringify'd
// (e.g. '"VACANT"', '"2028-12-12"', '500000'). Normalize back to raw values.
const parseValue = (v: FieldChange['previous_value']): unknown => {
  if (v === null || v === undefined) return null
  try {
    return JSON.parse(v)
  } catch {
    return v
  }
}

// A change is worth showing only if it represents a real transition.
const isMeaningfulChange = (c: PropertyHistoryChange): boolean => {
  if (isEmpty(c.current_value) && isEmpty(c.previous_value)) return false
  return JSON.stringify(c.previous_value) !== JSON.stringify(c.current_value)
}

// Property History is a property-level concept: the same underlying change can
// surface on both the FCL and private-seller listings, so we merge across all
// listings and dedupe identical events.
const dedupeKey = (c: PropertyHistoryChange): string =>
  `${c.field}|${c.changed_at}|${JSON.stringify(
    c.previous_value
  )}|${JSON.stringify(c.current_value)}`

/**
 * Fetches field-change history for every active listing of a property and
 * merges them into a single, de-duplicated, most-recent-first list.
 */
export const fetchPropertyHistory = async (
  listingIds: Array<string | number>
): Promise<PropertyHistoryChange[]> => {
  if (!listingIds || !listingIds.length) return []

  const perListing = await Promise.all(
    listingIds.map(id =>
      fetch
        .forAuctionGraph({
          query: GRAPH_PROPERTY_HISTORY_QUERY,
          variables: {
            listingId: String(id),
            fields: PROPERTY_HISTORY_FIELDS,
          },
        })
        .then(
          (res: {
            body?: {
              data?: {
                listing?: { field_change_timeline?: FieldChangeTimeline[] }
              }
            }
          }) => res?.body?.data?.listing?.field_change_timeline || []
        )
        .catch(() => [] as FieldChangeTimeline[])
    )
  )

  const flattened: PropertyHistoryChange[] = perListing
    .flat()
    .flatMap(timeline =>
      (timeline.changes || []).map(change => ({
        field: timeline.field,
        changed_at: change.changed_at,
        delta_label: change.delta_label,
        previous_value: parseValue(change.previous_value),
        current_value: parseValue(change.current_value),
      }))
    )
    .filter(isMeaningfulChange)

  const seen = new Set<string>()
  const merged = flattened.filter(c => {
    const key = dedupeKey(c)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  merged.sort(
    (a, b) =>
      new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
  )
  return merged
}
